import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy – Wichita Falls", address: "4701 Taft Blvd, Wichita Falls, TX 76308", hours: "Daily 7 AM – 10 PM" },
  { name: "Walgreens – Kemp Blvd", address: "3104 Kemp Blvd, Wichita Falls, TX 76308", hours: "Call for hours" },
  { name: "CVS Pharmacy – Lawrence Road", address: "2620 Lawrence Road, Wichita Falls, TX 76308", hours: "Daily 7 AM – 10 PM" },
  { name: "The UPS Store – Wichita Falls", address: "3304 Kell Blvd, Wichita Falls, TX 76309", hours: "Mon–Fri 8 AM – 6:30 PM, Sat 9 AM – 5 PM" },
  { name: "Walmart Supercenter – Photo Center", address: "4831 Kell Blvd, Wichita Falls, TX 76308", hours: "Daily – call for photo center hours" },
  { name: "FedEx Office – Wichita Falls", address: "1700 10th St, Wichita Falls, TX 76301", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Wichita County Tax Assessor-Collector",
    address: "600 Scott Ave, Suite 103, Wichita Falls, TX 76301",
    hours: "Mon–Fri, 8:00 AM – 4:30 PM",
    note: "Driver licenses, vehicle registration, titles. Appointment recommended. Handles REAL ID.",
    featured: true,
  },
  {
    name: "Texas DPS – Wichita Falls Driver License Office",
    address: "1800 Eighth St, Wichita Falls, TX 76301",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM (closed Tue)",
    note: "State driver license and ID issuance. Can have long wait times — appointment strongly recommended.",
    featured: false,
  },
];

export default function WichitaFallsHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Wichita Falls Travelers</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Here are local North Texas resources to help you gather everything you need before your next international trip.</p>
        </div>

        <div className="space-y-6">
          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0"><i className="ri-camera-line text-white text-lg"></i></div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Wichita Falls</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular locations near Wichita Falls:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button onClick={() => setPhotoExpanded(true)} className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors">
                  <i className="ri-add-circle-line text-sm"></i>Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed"><i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i><strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications.</p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0"><i className="ri-id-card-line text-white text-lg"></i></div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Wichita County DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">A valid government-issued photo ID is required for your passport application. Wichita County residents can obtain or renew their Texas driver&apos;s license or REAL ID at these locations.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                    <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                    <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0"><i className="ri-flight-takeoff-line text-white text-lg"></i></div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Wichita Falls Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {[
                  { code: "SPS", name: "Sheppard AFB/Wichita Falls Regional Airport", distance: "3 miles north of downtown", address: "1 Airport Blvd, Wichita Falls, TX 76305", note: "Commercial service to Dallas/Fort Worth. Limited international connections — most international travelers drive to DFW.", featured: false },
                  { code: "DFW", name: "Dallas/Fort Worth International Airport", distance: "About 120 miles south — ~2 hour drive", address: "2400 Aviation Drive, DFW Airport, TX 75261", note: "Primary international gateway for Wichita Falls travelers. Nonstop service to destinations across Europe, Asia, Latin America, and more.", featured: true },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary International</span>}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2"><i className="ri-road-map-line"></i><span>{airport.distance}</span></div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed">{airport.note}</p>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your DFW departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}